import { Grid } from "@mui/material";
import MxFormL3CityField from "@rf-l3/form/MxFormL3CityField";
import { memo, useCallback, useMemo, useState } from "react";
import { CPropertyFormKeys } from "@rf/property/module/components/property-form/util/schemas/property-schema/propertySchema";
import { useFormContext, useWatch } from "react-hook-form";
import MxFormL3SelectSectorField from "@rf-l3/form/MxForml3Sectors";
import HCheckArray from "@rf-helper/check-have-value";
import MxFormL3InputField from "@rf-l3/form/MxFormL3InputField";
import MxFormL3RushRadioDuoField from "@rf-l3/form/MxFormL3RushRadioDuo";
import MapFormSection from "./MapFormSection";
import MxFormSubTitleAndDescription from "@rf-l1/page/form/MxFormSubTitleAndDescription";

import { usePropertyFormFeaturesState } from "../../../store/slices/FormFeaturesSlice";
import usePropertyHelpTypeLocations from "../../../hooks/helps/usePropertyHelpTypeLocations";
import usePropertyFormHelp from "../../../hooks/helps/usePropertyFormHelp";
import MxMapLayout from "@rf-l1/map-v2/context/MxMapLayout";
import MxButtonStartIcon from "@rf-l1/buttons/MxButtonStartIcon";
import { IconLock, IconLockOpen2 } from "@tabler/icons-react";
import MxFormL3TextNumber from "@rf-l3/form/MxFormL3NumberField";
import { MxMapSearchAutocomplete } from "@rf-l1/map-v2/components/MxMapSearchAutocomplete";
import MxMapLayoutMap from "@rf-l1/map-v2/components/MxMapLayoutMap";

const LocationFormSection = () => {
  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <MxFormSubTitleAndDescription
          label="Ubicación"
          description="Completa la información de la ubicación de la propiedad, para que los clientes puedan encontrarla más fácilmente."
          isRequired
        />
      </Grid>
      <Grid item xs={12}>
        <MxMapLayout
          fieldsNames={{
            city: CPropertyFormKeys.city_property,
            sector: CPropertyFormKeys.sector_property,
            address: CPropertyFormKeys.address_property,
            lat: CPropertyFormKeys.latitude_property,
            lng: CPropertyFormKeys.longitude_property,
            direction: CPropertyFormKeys.address_property
          }}
        >
          <Grid container spacing={2} item xs={12}>
            <Grid container item xs={12} md={10} spacing={2}>
              <CityAndSectorFormSection />
              <Grid item xs={12} md={4}>
                <MxFormL3InputField
                  label="Dirección"
                  withreacthookform={1}
                  name={CPropertyFormKeys.address_property}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <MxFormL3InputField
                  label="Punto de referencia"
                  withreacthookform={1}
                  name={CPropertyFormKeys.reference_point_property}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <LocationPositionFormSection />
              </Grid>
              <Grid item xs={12} />
              <Grid item xs={12} md={4}>
                <MxMapSearchAutocomplete />
              </Grid>
              <ManualModeZone />
            </Grid>

            <Grid item xs={12}>
              <MxMapLayoutMap gestureHandling="greedy" />
            </Grid>
          </Grid>
        </MxMapLayout>
      </Grid>
    </Grid>
  );
};

export default LocationFormSection;

const CityAndSectorFormSection = memo(() => {
  const { setValue, control } = useFormContext();

  const CityField = useWatch({
    name: CPropertyFormKeys.city_property,
    defaultValue: [],
    control
  });

  const handleUpdateCity = useCallback(() => {
    setValue(CPropertyFormKeys.sector_property, []);
  }, []);

  const CityId = useMemo(() => {
    return HCheckArray(CityField) ? CityField[0].value : undefined;
  }, [CityField]);

  return (
    <>
      <Grid item xs={12} md={4}>
        <MxFormL3CityField
          name={CPropertyFormKeys.city_property}
          label="Ciudad"
          withreacthookform={1}
          onSelect={handleUpdateCity}
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <MxFormL3SelectSectorField
          name={CPropertyFormKeys.sector_property}
          label="Sector"
          cityId={CityId}
          disabled={!CityId}
          useCustomComponent={false}
          withreacthookform={1}
        />
      </Grid>
    </>
  );
});

const LocationPositionFormSection = () => {
  const { PROPERTY_TYPE } = usePropertyFormHelp();
  const { isProject } = usePropertyHelpTypeLocations();

  const { forProject, forProperty } = usePropertyFormFeaturesState();

  if (PROPERTY_TYPE.ESTATE) {
    return null;
  }

  return (
    <MxFormL3RushRadioDuoField
      name={CPropertyFormKeys.position_property}
      label="Posición"
      withreacthookform={1}
      items={
        isProject ? forProject?.project.location ?? [] : forProperty?.location ?? []
      }
    />
  );
};

const ManualModeZone = () => {
  const [manual, setManualMode] = useState(false);
  return (
    <>
      <Grid item xs="auto" minWidth={200}>
        <MxButtonStartIcon
          onClick={() => setManualMode(mode => !mode)}
          label={!manual ? "Ubicación manual" : "Ubicación automática"}
          bgColorIcon="primary.main"
          colorText="grey.800"
          icon={
            !manual ? (
              <IconLockOpen2 size={19} color="white" />
            ) : (
              <IconLock size={19} color="white" />
            )
          }
        />
      </Grid>

      {manual && (
        <Grid item xs={12} md={6} lg={2.56}>
          <MxFormL3TextNumber
            label={"Latitud"}
            withreacthookform={1}
            name={CPropertyFormKeys.latitude_property}
          />
        </Grid>
      )}

      {manual && (
        <Grid item xs={12} md={6} lg={2.56}>
          <MxFormL3TextNumber
            label={"Longitud"}
            withreacthookform={1}
            name={CPropertyFormKeys.longitude_property}
          />
        </Grid>
      )}
    </>
  );
};
